/**
 * Read one version of a client's 245D plan and acknowledge it.
 *
 * The server serves the plan only to staff assigned to the client, without
 * date of birth, SSN or member ids; signatures show as "Signed". The
 * acknowledgment names the version read (contentFingerprint): if the plan
 * changed meanwhile the server answers 409 and the latest version is loaded.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { CheckCircleIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useVersionedApi } from '../../../utils/versionedApi';
import useDialogFocus from '../useDialogFocus';
import { STATE_BADGE, STATE_LABEL, acknowledgePath, dueText, errorMessage, formatDay, formatTimestamp, personName, planPath } from './planAcknowledgmentModel';

const SECONDARY = 'min-h-10 px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50';
const PRIMARY = 'min-h-10 px-4 rounded-lg bg-blue-700 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * @param {object} props
 * @param {string} props.documentId
 * @param {Function} props.onClose
 * @param {Function} [props.onAcknowledged] called with the document id after a recorded acknowledgment
 */
export default function PlanReaderDialog({ documentId, onClose, onAcknowledged }) {
  const api = useVersionedApi('v1');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revision, setRevision] = useState(0);
  const panelRef = useRef(null);
  useDialogFocus(panelRef, { onClose, canClose: () => !submitting });

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    api.get(planPath(documentId))
      .then((response) => { if (active) setPlan(response?.data?.data || null); })
      .catch((error) => {
        if (!active) return;
        setPlan(null);
        setLoadError(error?.response?.status === 404 ? 'This plan is not available to you. It may have been replaced by a newer document.' : errorMessage(error, 'The plan could not be loaded. Try again.'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, documentId, revision]);

  const acknowledge = useCallback(async () => {
    if (!plan || !confirmed || submitting) return;
    setSubmitting(true);
    try {
      await api.post(acknowledgePath(plan.documentId), { contentFingerprint: plan.contentFingerprint, confirmed: true });
      toast.success('Thank you. Your acknowledgment is recorded.');
      onAcknowledged?.(plan.documentId);
      onClose();
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.warning('A newer version of this plan was saved. The latest version is now shown. Read it before acknowledging.');
        setConfirmed(false);
        setRevision((value) => value + 1);
      } else {
        toast.error(errorMessage(error, 'Your acknowledgment could not be recorded. Try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [api, confirmed, onAcknowledged, onClose, plan, submitting]);

  const acknowledged = plan?.state === 'acknowledged';
  // A portal: it may open from inside another dialog or a disabled fieldset
  // (a service note being written), and must sit above and outside both.
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="plan-reader-title">
      <div ref={panelRef} tabIndex={-1} className="flex max-h-[90dvh] w-full max-w-3xl flex-col rounded-xl border border-gray-200 bg-white shadow-xl focus:outline-none dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 shrink-0 text-teal-700 dark:text-teal-400" aria-hidden="true" />
              <h2 id="plan-reader-title" className="m-0 text-base font-semibold text-gray-900 dark:text-white">{plan ? plan.title : 'Plan'}</h2>
            </div>
            {plan && (
              <p className="m-0 mt-1 text-sm text-gray-600 dark:text-gray-400">
                {`${personName(plan.patient)} · Version ${plan.version} · Current since ${formatDay(plan.currentSince)}`}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} disabled={submitting} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700" aria-label="Close">
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div role="status" className="flex justify-center py-10">
              <div aria-hidden="true" className="h-7 w-7 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600 dark:border-gray-700 dark:border-t-teal-500" />
              <span className="sr-only">Loading the plan</span>
            </div>
          )}
          {!loading && loadError && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{loadError}</p>}
          {!loading && plan && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATE_BADGE[plan.state] || STATE_BADGE.pending}`}>{STATE_LABEL[plan.state] || plan.state}</span>
                {!acknowledged && <span className="text-gray-700 dark:text-gray-300">{dueText(plan)}</span>}
                {acknowledged && <span className="text-gray-700 dark:text-gray-300">{`You acknowledged this version on ${formatTimestamp(plan.acknowledgedAt)}.`}</span>}
              </div>
              {plan.sections.length === 0 && <p className="text-sm text-gray-600 dark:text-gray-400">This plan has no filled-in sections to show.</p>}
              {plan.sections.map((section) => (
                <section key={section.title} className="rounded-lg border border-gray-200 border-l-4 border-l-teal-600 px-4 py-3 dark:border-gray-700 dark:border-l-teal-500">
                  <h3 className="m-0 text-sm font-semibold text-gray-900 dark:text-white">{section.title}</h3>
                  <dl className="m-0 mt-2 space-y-2">
                    {section.fields.map((field) => (
                      <div key={`${section.title}-${field.label}`}>
                        <dt className="text-xs font-medium text-gray-600 dark:text-gray-400">{field.label}</dt>
                        <dd className="m-0 whitespace-pre-wrap break-words text-sm text-gray-900 dark:text-gray-100">{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          {plan && !acknowledged ? (
            <label htmlFor="plan-reader-confirm" className="flex min-h-10 cursor-pointer items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
              <input id="plan-reader-confirm" type="checkbox" checked={confirmed} disabled={submitting} onChange={(event) => setConfirmed(event.target.checked)} className="h-5 w-5 rounded border-gray-300 text-blue-700 focus:ring-blue-500 dark:border-gray-600" />
              {plan.statement || 'I have read this version of the plan.'}
            </label>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" className={SECONDARY} onClick={onClose} disabled={submitting}>{acknowledged ? 'Close' : 'Not now'}</button>
            {plan && !acknowledged && (
              <button type="button" className={`${PRIMARY} inline-flex items-center gap-2`} onClick={acknowledge} disabled={!confirmed || submitting}>
                <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                {submitting ? 'Recording…' : 'Acknowledge'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
